import React from 'react';
import Button from '@app/react-common/components/button/Button';
import Tooltip from '@app/react-common/components/tooltip/Tooltip';
import { DeliveryAreaModeGroup } from '../BulkEditModal';
import { ShipToFormData } from './ShipToEditView';
import { countryCodeToDisplayNameMap, extendedCountryToStates } from '@pdqdev/pdq-shared/dist/utils/Country-utils';
import { isInternationalMethod } from '../utils/bulkEditUtils';
import { CollapsibleSection } from '../components';
import { BasePreviewViewProps, SelectedMethodInfo } from './types';

interface ShipToPreviewViewProps extends BasePreviewViewProps {
  formData: ShipToFormData;
  deliveryAreaMode?: DeliveryAreaModeGroup | null;
}

const getStateLabel = (countryCode: string, stateCode: string): string => {
  const states = extendedCountryToStates[countryCode] || [];
  const state = states.find((s) => s.value === stateCode);
  return state?.label || stateCode;
};

const getCurrentStatesControl = (method: SelectedMethodInfo): Record<string, string[]> => {
  if (isInternationalMethod(method)) {
    return method.internationalSettings?.countriesAndRegions || {};
  }
  const polygon = method.polygonInputValues?.[0];
  if (polygon?.listedStatesControl && Object.keys(polygon.listedStatesControl).length > 0) {
    return polygon.listedStatesControl;
  }
  return {};
};

const getCurrentCities = (method: SelectedMethodInfo): string[] => {
  if (isInternationalMethod(method)) {
    return [];
  }
  return method.polygonInputValues?.[0]?.listedCities || [];
};

const getCurrentZones = (method: SelectedMethodInfo): Array<{ label: string; value: boolean }> => {
  if (isInternationalMethod(method)) {
    return [];
  }
  return method.polygonInputValues?.[0]?.zonesList || [];
};

const getCurrentZipCodes = (method: SelectedMethodInfo): string[] => {
  if (isInternationalMethod(method)) {
    return [];
  }
  const zipCodesStr = method.polygonInputValues?.[0]?.zipCodes || '';
  if (!zipCodesStr.trim()) return [];
  return zipCodesStr
    .split(',')
    .map((z) => z.trim())
    .filter((z) => z.length > 0);
};

const getCurrentExcludedStates = (method: SelectedMethodInfo): string[] => {
  return method.excludedStates || method.polygonInputValues?.[0]?.excludedStates || [];
};

const willZipCodesChange = (currentZipCodes: string[], toChange: string[], action: 'add' | 'remove'): boolean => {
  if (toChange.length === 0) return false;

  if (action === 'add') {
    return toChange.some((z) => !currentZipCodes.includes(z));
  } else {
    return toChange.some((z) => currentZipCodes.includes(z));
  }
};

const willZonesChange = (
  currentZones: Array<{ label: string; value: boolean }>,
  toChange: string[],
  action: 'add' | 'remove',
): boolean => {
  if (toChange.length === 0) return false;

  for (const zone of toChange) {
    const currentZone = currentZones.find((z) => z.label === zone);
    const isCurrentlyEnabled = currentZone?.value ?? false;

    if (action === 'add' && !isCurrentlyEnabled) return true;
    if (action === 'remove' && isCurrentlyEnabled) return true;
  }
  return false;
};

const willStatesChange = (
  current: Record<string, string[]>,
  toChange: Record<string, string[]>,
  action: 'add' | 'remove',
): boolean => {
  if (Object.keys(toChange).length === 0) return false;

  for (const [country, states] of Object.entries(toChange)) {
    const currentStates = current[country] || [];
    if (action === 'add') {
      const newStates = states.filter((s) => !currentStates.includes(s));
      if (newStates.length > 0) return true;
    } else {
      const removableStates = states.filter((s) => currentStates.includes(s));
      if (removableStates.length > 0) return true;
    }
  }
  return false;
};

const willCitiesChange = (current: string[], toChange: string[], action: 'add' | 'remove'): boolean => {
  if (toChange.length === 0) return false;
  const currentLower = current.map((c) => c.toLowerCase());
  const toChangeLower = toChange.map((c) => c.toLowerCase());

  if (action === 'add') {
    return toChangeLower.some((c) => !currentLower.includes(c));
  } else {
    return toChangeLower.some((c) => currentLower.includes(c));
  }
};

const willExcludedStatesChange = (current: string[], toChange: string[], action: 'add' | 'remove'): boolean => {
  if (toChange.length === 0) return false;
  const currentLower = current.map((s) => s.toLowerCase());
  const toChangeLower = toChange.map((s) => s.toLowerCase());

  if (action === 'add') {
    return toChangeLower.some((s) => !currentLower.includes(s));
  } else {
    return toChangeLower.some((s) => currentLower.includes(s));
  }
};

// Sub-components for each preview section
interface StatesPreviewSectionProps {
  statesControl: Record<string, string[]>;
  isInternational: boolean;
  isAddAction: boolean;
  formatStatesToDisplay: (statesControl: Record<string, string[]>) => string[];
}

const StatesPreviewSection = ({
  statesControl,
  isInternational,
  isAddAction,
  formatStatesToDisplay,
}: StatesPreviewSectionProps) => (
  <>
    {formatStatesToDisplay(statesControl).map((item, idx) => (
      <div key={idx} className="bulk-edit-modal__preview-item__change">
        <span className="bulk-edit-modal__preview-item__change__label">
          Ship To {isInternational ? 'Region' : 'State'}:
        </span>
        <span
          className={
            isAddAction ? 'bulk-edit-modal__preview-item__change__new' : 'bulk-edit-modal__preview-item__change__old'
          }>
          {item}
        </span>
      </div>
    ))}
  </>
);

interface CitiesPreviewSectionProps {
  cities: string[];
  isAddAction: boolean;
}

const CitiesPreviewSection = ({ cities, isAddAction }: CitiesPreviewSectionProps) => (
  <>
    {cities.map((city, idx) => (
      <div key={idx} className="bulk-edit-modal__preview-item__change">
        <span className="bulk-edit-modal__preview-item__change__label">Ship To City:</span>
        <span
          className={
            isAddAction ? 'bulk-edit-modal__preview-item__change__new' : 'bulk-edit-modal__preview-item__change__old'
          }>
          {city}
        </span>
      </div>
    ))}
  </>
);

interface ZonesPreviewSectionProps {
  zones: string[];
  currentZones: Array<{ label: string; value: boolean }>;
  isAddAction: boolean;
}

const ZonesPreviewSection = ({ zones, currentZones, isAddAction }: ZonesPreviewSectionProps) => (
  <>
    {zones.map((zone, idx) => {
      const currentZone = currentZones.find((z) => z.label === zone);
      const isCurrentlyEnabled = currentZone?.value ?? false;
      const willActuallyChange = isAddAction ? !isCurrentlyEnabled : isCurrentlyEnabled;
      if (!willActuallyChange) return null;

      return (
        <div key={idx} className="bulk-edit-modal__preview-item__change">
          <span className="bulk-edit-modal__preview-item__change__label">
            {isAddAction ? 'Enable Zone:' : 'Disable Zone:'}
          </span>
          <span
            className={
              isAddAction ? 'bulk-edit-modal__preview-item__change__new' : 'bulk-edit-modal__preview-item__change__old'
            }>
            {zone}
          </span>
        </div>
      );
    })}
  </>
);

interface ZipCodesPreviewSectionProps {
  zipCodes: string[];
  currentZipCodes: string[];
  isAddAction: boolean;
}

const ZipCodesPreviewSection = ({ zipCodes, currentZipCodes, isAddAction }: ZipCodesPreviewSectionProps) => (
  <>
    {zipCodes.map((zipCode, idx) => {
      const isCurrentlyInList = currentZipCodes.includes(zipCode);
      const willActuallyChange = isAddAction ? !isCurrentlyInList : isCurrentlyInList;
      if (!willActuallyChange) return null;

      return (
        <div key={idx} className="bulk-edit-modal__preview-item__change">
          <span className="bulk-edit-modal__preview-item__change__label">
            {isAddAction ? 'Add Zip Code:' : 'Remove Zip Code:'}
          </span>
          <span
            className={
              isAddAction ? 'bulk-edit-modal__preview-item__change__new' : 'bulk-edit-modal__preview-item__change__old'
            }>
            {zipCode}
          </span>
        </div>
      );
    })}
  </>
);

interface ExcludedStatesPreviewSectionProps {
  excludedStates: string[];
  currentExcludedStates: string[];
  isAddAction: boolean;
}

const ExcludedStatesPreviewSection = ({
  excludedStates,
  currentExcludedStates,
  isAddAction,
}: ExcludedStatesPreviewSectionProps) => (
  <>
    {excludedStates.map((state, idx) => {
      const isCurrentlyExcluded = currentExcludedStates.some((s) => s.toLowerCase() === state.toLowerCase());
      const willActuallyChange = isAddAction ? !isCurrentlyExcluded : isCurrentlyExcluded;
      if (!willActuallyChange) return null;

      return (
        <div key={idx} className="bulk-edit-modal__preview-item__change">
          <span className="bulk-edit-modal__preview-item__change__label">
            {isAddAction ? 'Exclude State:' : 'Remove Exclusion:'}
          </span>
          <span
            className={
              isAddAction ? 'bulk-edit-modal__preview-item__change__old' : 'bulk-edit-modal__preview-item__change__new'
            }>
            {state}
          </span>
        </div>
      );
    })}
  </>
);

export const ShipToPreviewView = ({
  onBack,
  onClose,
  onSaveChanges,
  formData,
  selectedMethods,
  deliveryAreaMode = null,
}: ShipToPreviewViewProps) => {
  const hasStatesInput =
    Object.keys(formData.statesControl).length > 0 &&
    Object.values(formData.statesControl).some((states) => states.length > 0);
  const hasCitiesInput = formData.cities.length > 0;
  const hasZonesInput = (formData.zones?.length ?? 0) > 0;
  const hasZipCodesInput = (formData.zipCodes?.length ?? 0) > 0;
  const hasExcludedStatesInput = (formData.excludedStates?.length ?? 0) > 0;
  const isInternational = selectedMethods.length > 0 && isInternationalMethod(selectedMethods[0]);
  const isAddAction = formData.action === 'add';

  const formatStatesToDisplay = (statesControl: Record<string, string[]>): string[] => {
    const items: string[] = [];
    Object.entries(statesControl).forEach(([countryCode, stateCodes]) => {
      const countryName = countryCodeToDisplayNameMap[countryCode] || countryCode;
      if (stateCodes.length === 0) {
        items.push(countryName);
      } else {
        stateCodes.forEach((code) => {
          items.push(`${countryName}: ${getStateLabel(countryCode, code)}`);
        });
      }
    });
    return items;
  };

  const methodHasChanges = (method: SelectedMethodInfo): boolean => {
    const currentStates = getCurrentStatesControl(method);
    const currentCities = getCurrentCities(method);
    const currentZones = getCurrentZones(method);
    const currentZipCodes = getCurrentZipCodes(method);
    const currentExcludedStates = getCurrentExcludedStates(method);

    const statesWillChange = hasStatesInput && willStatesChange(currentStates, formData.statesControl, formData.action);
    const citiesWillChange = hasCitiesInput && willCitiesChange(currentCities, formData.cities, formData.action);
    const zonesWillChange = hasZonesInput && willZonesChange(currentZones, formData.zones || [], formData.action);
    const zipCodesWillChange =
      hasZipCodesInput && willZipCodesChange(currentZipCodes, formData.zipCodes || [], formData.action);
    const excludedStatesWillChange =
      hasExcludedStatesInput &&
      willExcludedStatesChange(currentExcludedStates, formData.excludedStates || [], formData.action);

    return statesWillChange || citiesWillChange || zonesWillChange || zipCodesWillChange || excludedStatesWillChange;
  };

  const changedMethods = selectedMethods.filter(methodHasChanges);
  const unchangedMethods = selectedMethods.filter((m) => !methodHasChanges(m));
  const affectedCount = changedMethods.length;
  const isSaveDisabled = affectedCount === 0;

  return (
    <div className="bulk-edit-modal__view">
      <div className="bulk-edit-modal__header bulk-edit-modal__header--with-subtitle">
        <div className="bulk-edit-modal__header__left">
          <div className="bulk-edit-modal__header__titles">
            <h2>Confirm changes</h2>
            <span className="bulk-edit-modal__header__subtitle">
              {affectedCount} of {selectedMethods.length} Methods Will Be Affected
            </span>
          </div>
        </div>
        <Button icon="close" variant="ghost" size="small" onClick={onClose} />
      </div>
      <div className="bulk-edit-modal__content">
        {changedMethods.length > 0 && (
          <CollapsibleSection title="Changed" count={changedMethods.length} defaultOpen={true}>
            {changedMethods.map((method) => {
              const currentStates = getCurrentStatesControl(method);
              const currentCities = getCurrentCities(method);
              const currentZones = getCurrentZones(method);
              const currentZipCodes = getCurrentZipCodes(method);
              const currentExcludedStates = getCurrentExcludedStates(method);

              const statesWillChange =
                hasStatesInput && willStatesChange(currentStates, formData.statesControl, formData.action);
              const citiesWillChange =
                hasCitiesInput && willCitiesChange(currentCities, formData.cities, formData.action);
              const zonesWillChange =
                hasZonesInput && willZonesChange(currentZones, formData.zones || [], formData.action);
              const zipCodesWillChange =
                hasZipCodesInput && willZipCodesChange(currentZipCodes, formData.zipCodes || [], formData.action);
              const excludedStatesWillChange =
                hasExcludedStatesInput &&
                willExcludedStatesChange(currentExcludedStates, formData.excludedStates || [], formData.action);

              return (
                <div key={method.id} className="bulk-edit-modal__preview-item">
                  <div className="bulk-edit-modal__preview-item__header">{method.name || '(No name)'}</div>
                  <div className="bulk-edit-modal__preview-item__group">{method.groupName}</div>

                  {statesWillChange && (
                    <StatesPreviewSection
                      statesControl={formData.statesControl}
                      isInternational={isInternational}
                      isAddAction={isAddAction}
                      formatStatesToDisplay={formatStatesToDisplay}
                    />
                  )}

                  {citiesWillChange && !isInternational && (
                    <CitiesPreviewSection cities={formData.cities} isAddAction={isAddAction} />
                  )}

                  {zonesWillChange && (
                    <ZonesPreviewSection
                      zones={formData.zones || []}
                      currentZones={currentZones}
                      isAddAction={isAddAction}
                    />
                  )}

                  {zipCodesWillChange && (
                    <ZipCodesPreviewSection
                      zipCodes={formData.zipCodes || []}
                      currentZipCodes={currentZipCodes}
                      isAddAction={isAddAction}
                    />
                  )}

                  {excludedStatesWillChange && (
                    <ExcludedStatesPreviewSection
                      excludedStates={formData.excludedStates || []}
                      currentExcludedStates={currentExcludedStates}
                      isAddAction={isAddAction}
                    />
                  )}
                </div>
              );
            })}
          </CollapsibleSection>
        )}
        {unchangedMethods.length > 0 && (
          <CollapsibleSection title="Unchanged" count={unchangedMethods.length} defaultOpen={false}>
            {unchangedMethods.map((method) => (
              <div key={method.id} className="bulk-edit-modal__preview-item">
                <div className="bulk-edit-modal__preview-item__header">{method.name || '(No name)'}</div>
                <div className="bulk-edit-modal__preview-item__group">{method.groupName}</div>
                <div className="bulk-edit-modal__preview-item__change">
                  <span className="bulk-edit-modal__preview-item__change__unchanged">(unchanged)</span>
                </div>
              </div>
            ))}
          </CollapsibleSection>
        )}
      </div>
      <div className="bulk-edit-modal__footer">
        <Button text="Back" onClick={onBack} size="medium" variant="secondary" />
        {isSaveDisabled ? (
          <Tooltip tooltipText="No methods will be affected by this change" maxWidth={220}>
            <Button
              text="Save Changes"
              onClick={() => onSaveChanges(changedMethods.map((m) => m.id))}
              size="medium"
              variant="primary"
              disabled
            />
          </Tooltip>
        ) : (
          <div className="bulk-edit-modal__button-wrapper">
            <Button
              text="Save Changes"
              onClick={() => onSaveChanges(changedMethods.map((m) => m.id))}
              size="medium"
              variant="primary"
            />
          </div>
        )}
      </div>
    </div>
  );
};
